import styled, { css } from 'styled-components';
import { colors, fontSize, fontWeight, media, spacer } from 'settings/style';
import { LabelColors } from '@polgozar/utils';

export const StyledWrapper = styled.div`
  padding: 26px 1.5rem 36px;
  max-width: 1500px;
  margin: 0 auto;
  ${media.md`
    padding: 26px 16px 36px;
  `}
`;

export const AnimatedCalendarView = styled.div`
  animation: calendarViewEnter 260ms ease;

  @keyframes calendarViewEnter {
    from {
      opacity: 0;
      transform: translateY(8px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  gap: 12px;
  margin-bottom: 18px;

  ${media.md`
    position: static;
    justify-content: flex-start;
  `}
`;

export const GroupSelectWrapper = styled.div`
  .ant-select {
    min-width: 420px;
  }

  ${media.md`
    flex: 1;

    .ant-select {
      min-width: 0;
      width: 100%;
    }
  `}
`;

export const ViewSwitch = styled.div`
  display: inline-flex;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #d5d7da;
  background: #efefef;
  overflow: hidden;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);

  ${media.md`
    position: static;
    left: auto;
    top: auto;
    transform: none;
    align-self: center;
  `}
`;

export const ViewSwitchButton = styled.button<{ active: boolean }>`
  border: none;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ active }) => (active ? '#424c5e' : '#6f7787')};
  background: ${({ active }) => (active ? '#ffffff' : 'transparent')};
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease;
`;

export const CalendarWrapper = styled.div`
  display: block;
  border-radius: 10px;
  border: 1px solid #d6d7db;
  overflow: hidden;
  box-shadow: 0 6px 28px 0 rgba(120, 130, 140, 0.18);
`;

export const PersonalEventFormSection = styled.div`
  border-top: 1px solid #d6d7db;
  padding: 16px 16px 18px;
  background: #fff;

  ${media.md`
    padding: 14px 12px 16px;
  `}
`;

export const PersonalEventFormSectionTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  color: #2f3441;
`;

export const EventTitle = styled.p`
  font-weight: ${fontWeight.bold};
  padding-bottom: ${spacer.md};
`;

export const EventWrapper = styled.div`
  margin-top: 6px;
  border-radius: 10px;
  border: 1px solid #d6d7db;
  padding: 14px;
  box-shadow: 0 6px 28px 0 rgba(120, 130, 140, 0.18);

  > * + * {
    margin-top: ${spacer.sm};
  }
`;

export const WeekDayWrapper = styled.div`
  min-width: 0;
  text-align: center;
`;

/** Full weekday names on desktop; hidden on mobile (see WeekdayLabelShort). */
export const WeekdayLabelLong = styled.span`
  ${media.md`
    display: none;
  `}
`;

/** Short weekday names (Mon, Tue, …) on mobile only. */
export const WeekdayLabelShort = styled.span`
  display: none;

  ${media.md`
    display: inline;
  `}
`;

export const WeekHeaderWrapper = styled.div`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  width: 100%;
  text-transform: none;
  font-weight: 600;
  color: white;
  font-size: 13px;
  padding: 8px 0;
  background: ${colors.gray600};
  border-bottom: 1px solid #d3d5d8;
`;

/** Today cell: blue fill (vs gold semi-important); red border + day number. */
const calendarTodayBg = '#e8f4ff';
const calendarTodayRed = '#ff0000';

interface DayWrapperProps {
  disabled: boolean;
  selected: boolean;
  draftDate?: boolean;
  labelColor?: LabelColors;
  bgImage?: string;
  isToday: boolean;
}

export const DayWrapper = styled.div<DayWrapperProps>`
  min-width: 0;
  position: relative;
  border-right: 1px solid #d8dadd;
  cursor: pointer;
  transition: 0.2s ease-out;
  border-left: 2px solid transparent;

  ${({ labelColor }) =>
    labelColor &&
    css`
      background-color: ${labelColor};
    `}

  ${({ bgImage }) =>
    bgImage &&
    css`
      &:before {
        content: '';
        background-image: url(${bgImage});
        background-repeat: no-repeat;
        background-position: center 96%;
        background-size: 50%;
        position: absolute;
        inset: 0;
        opacity: 0.8;
      }
    `}

  &:last-child {
    border-right: none;
  }

  ${({ selected, disabled }) =>
    selected &&
    !disabled &&
    css`
      border-left-color: ${colors.accent};
      background-color: #f8f0d7;
    `}

  ${({ draftDate, disabled }) =>
    draftDate &&
    !disabled &&
    css`
      box-shadow: inset 0 0 0 2px #1a2744;
    `}

  ${({ isToday, disabled }) =>
    isToday &&
    css`
      background-color: ${disabled ? '#f5f5f6' : calendarTodayBg};
      border: 1px solid ${disabled ? '#d4d4d8' : calendarTodayRed};
      border-radius: 4px;
      box-shadow: inset 0 0 0 1px ${disabled ? 'transparent' : 'rgba(198, 40, 40, 0.2)'};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
      pointer-events: none;
      background-color: #f5f5f6;
    `}
`;

export const EventSpaceBar = styled.div`
  padding-bottom: 1rem;
  ${media.md`
    padding-bottom: 0.7rem;
  `}
`;

export const InnerContent = styled.div`
  height: 9.4rem;
  display: flex;
  flex-direction: column;
  padding: 0.5rem;
  box-sizing: border-box;
  ${media.md`
    height: 6rem;
    padding: 0.3rem;
  `}
`;

/** Single row: legacy side-by-side layout (unused for Fasli/Khayyām stacked cells). */
export const DayHeaderRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  flex-shrink: 0;
`;

/** Day cell: Gregorian (top); middle + name depend on Fasli vs Magian view (only one calendar’s day number). */
export const DayCellInfoStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  flex-shrink: 0;
  min-width: 0;
  gap: 2px;
`;

interface DayNumberProps {
  disabled: boolean;
  isToday?: boolean;
}

/** Gregorian day of month — top of cell, red */
export const DayCellGregorian = styled.span<DayNumberProps>`
  font-weight: 600;
  line-height: 1.15;
  font-size: 0.72rem;
  color: #c62828;
  text-align: center;
  width: 100%;

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
    `}

  ${media.md`
    font-size: 0.6rem;
  `}
`;

/** Prominent day number: Fasli cycle day (Fasli view) or Jalali j-day (Magian view) */
export const DayCellMagian = styled.span<DayNumberProps>`
  font-weight: 700;
  line-height: 1.1;
  color: #101828;
  font-size: 1.35rem;
  text-align: center;
  width: 100%;

  ${({ isToday, disabled }) =>
    isToday &&
    !disabled &&
    css`
      font-weight: 800;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
    `}

  ${media.md`
    font-size: 1rem;
  `}
`;

/** Priest / liturgical day name — under the prominent number */
export const DayCellFasliName = styled.span<DayNumberProps>`
  max-width: 100%;
  min-width: 0;
  font-size: 13px;
  line-height: 1.25;
  color: #101828;
  text-align: center;
  word-wrap: break-word;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  hyphens: auto;
  display: block;
  width: 100%;

  ${media.md`
    font-size: 8px;
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
    `}
`;

/** Fasli header: Fasli month + year, large black */
export const FasliMonthTitle = styled.p`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: Georgia, 'Times New Roman', serif;
  color: #101828;
  line-height: 1.2;

  ${media.md`
    font-size: 1.25rem;
  `}
`;

/** Gregorian months under Fasli / Khayyām title — smaller, red */
export const GregorianMonthSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
  font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif;
  color: #c62828;
  line-height: 1.35;
`;

/** Khayyām header: month + ZRE year */
export const KhayyamMonthTitle = styled.p`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  font-family: Georgia, 'Times New Roman', serif;
  color: #101828;
  line-height: 1.2;

  ${media.md`
    font-size: 1.25rem;
  `}
`;

interface Disabled {
  disabled: boolean;
}

/** Inline red accent for Gregorian labels where used */
export const GregorianRedText = styled.span`
  color: #c62828;
`;

export const GeorgianDay = styled.span<Disabled & { $gregorianRed?: boolean }>`
  font-weight: 700;
  line-height: 1;
  color: #303645;
  font-size: 1.1rem;

  ${({ $gregorianRed, disabled }) =>
    $gregorianRed &&
    !disabled &&
    css`
      color: #c62828;
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
    `}

  ${media.md`
    font-size: 0.75rem;
  `}
`;

export const PersianDay = styled.p<Disabled>`
  margin: 0;
  font-weight: 500;
  line-height: 1;
  font-size: 0.72rem;
  color: #a1a7b3;
  text-align: right;

  ${media.md`
    font-size: 0.6rem;
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #c7ccd6;
    `}
`;

export const ZDetails = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: center;
  width: 100%;
  min-width: 0;
  min-height: 0;
  margin-top: 5px;
`;

export const DayIndicators = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
  margin-top: auto;
  padding-top: 0.25rem;
`;

export const ZEvent = styled.div<Disabled>`
  max-width: 100%;
  min-width: 0;
  font-size: 12px;
  overflow-wrap: anywhere;
  word-break: break-word;
  white-space: normal;
  hyphens: auto;

  ${media.md`
    font-size: 7px;
    font-weight: ${fontWeight.bold};
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
    `}
`;

export const NaborDay = styled.div<Disabled>`
  border: 1px solid #56b100;
  color: #56b100;
  background-color: #00800017;
  padding: 0.2rem;
  border-radius: 5px;
  font-size: 0.7rem;

  ${media.md`
    border-radius: 3px;
    font-size: 0.4rem;
    padding: 0.1rem;
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
      border-color: #b4b9c3;
    `}
`;

export const PartyDay = styled.div`
  border: 1px solid #ff0080;
  color: #ff0080;
  background-color: #ff008021;
  padding: 0.2rem 0.4rem;
  border-radius: 5px;
  font-size: 0.7rem;

  ${media.md`
    border-radius: 3px;
    font-size: 0.4rem;
    padding: 0.1rem 0.2rem;
  `}
`;

export const EventDay = styled.div`
  border: 1px solid #ffb300;
  background-color: #ffb3008f;
  padding: 0.2rem;
  border-radius: 5px;
  font-size: 0.7rem;

  ${media.md`
    border-radius: 3px;
    font-size: 0.4rem;
    padding: 0.1rem;
  `}
`;

export const PersonalEventDay = styled.div<Disabled & { $color: string }>`
  border: 1px solid ${({ $color }) => $color};
  color: ${({ $color }) => $color};
  background-color: ${({ $color }) => `${$color}17`};
  padding: 0.2rem;
  border-radius: 5px;
  font-size: 0.7rem;

  ${media.md`
    border-radius: 3px;
    font-size: 0.4rem;
    padding: 0.1rem;
  `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: #b4b9c3;
      border-color: #b4b9c3;
      background-color: transparent;
    `}
`;

interface EventBarProps {
  span: number;
  disabled: boolean;
  openEnded?: boolean;
  openStarted?: boolean;
  type?: 'gohanbar' | 'ziyarat';
}

const EventBarTypes = {
  gohanbar: colors.success,
  ziyarat: colors.accent,
} as const;

export const EventBar = styled.div<EventBarProps>`
  position: absolute;
  background: ${({ disabled, type }) => (disabled ? '#b8beca' : EventBarTypes[type] || colors.accent)};
  left: 0;
  z-index: 9;
  text-align: left;
  padding-left: 10px;
  width: calc(((100% + 2px) * ${({ span }) => span}) - ${({ openStarted }) => (openStarted ? '0px' : '8px')});
  margin-left: 2px;
  border-radius: 5px;
  color: white;

  ${({ openStarted }) =>
    openStarted &&
    css`
      border-bottom-left-radius: 0;
      border-top-left-radius: 0;
      margin-left: -2px;
    `}

  ${({ openEnded }) =>
    openEnded &&
    css`
      border-bottom-right-radius: 0;
      border-top-right-radius: 0;
    `}
`;

export const WeekRowWrapper = styled.div`
  margin: 0;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  width: 100%;
  border-bottom: 1px solid #d8dadd;

  &:last-child {
    border-bottom: none;
  }
`;

export const HeaderNavWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 20px 18px;
  border-bottom: 1px solid #d6d7db;

  ${media.md`
    flex-direction: column;
    gap: 10px;
    padding: 20px 13px;
  `}
`;

export const HeaderNavTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const GeorgianDate = styled.p`
  margin: 0;
  color: #343b4b;
  font-size: 22px;
  font-family: Georgia, 'Times New Roman', serif;
`;

export const PersianDate = styled.p`
  margin: 0;
  font-size: 14px;
  color: #6a7280;
`;

export const HeaderNavAction = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  .ant-btn {
    border-radius: 8px;
    border-color: #d3d5d8;
    color: #3c4352;
    box-shadow: none;
    height: 34px;

    &:hover,
    &:focus,
    &:active {
      border-color: #bfc4cb !important;
      color: #2f3441 !important;
      background: #ffffff !important;
      box-shadow: none !important;
    }
  }

  ${media.md`
    width: 100%;
    justify-content: end;
  `}
`;

export const CalendarDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const DetailsCard = styled.div`
  border-radius: 10px;
  border: 1px solid #d6d7db;
  padding: 18px;
  box-shadow: 0 6px 28px 0 rgba(120, 130, 140, 0.18);
  button {
    width: 100%;
  }
`;

export const PersonalEventsCard = styled.div`
  border-radius: 10px;
  border: 1px solid #d6d7db;
  padding: 18px;
  box-shadow: 0 6px 28px 0 rgba(120, 130, 140, 0.18);

  > button {
    width: 100%;
    margin-top: 14px;
  }
`;

export const PersonalEventsList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const PersonalEventItem = styled.li`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 12px;
  border-radius: 8px;
  background: #f8f9fb;
  border: 1px solid #e8eaef;
`;

export const PersonalEventActions = styled.div`
  flex-shrink: 0;
  display: flex;
  gap: 6px;
`;

const personalEventIconButton = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
`;

export const PersonalEventEditButton = styled.button`
  ${personalEventIconButton}
  border: 1px solid #e4e7ec;
  color: #1a2744;

  &:hover {
    border-color: #1a2744;
    background: #f8f9fb;
  }
`;

export const PersonalEventDeleteButton = styled.button`
  ${personalEventIconButton}
  border: 1px solid #f5c2c7;
  color: #cf1322;

  &:hover {
    border-color: #cf1322;
    background: #fff1f0;
  }
`;

export const PersonalEventColor = styled.span<{ $color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-top: 5px;
  flex-shrink: 0;
  background-color: ${({ $color }) => $color || '#1a2744'};
`;

export const PersonalEventBody = styled.div`
  flex: 1;
  min-width: 0;
`;

export const PersonalEventSubject = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #2f3441;
  line-height: 1.3;
`;

export const PersonalEventDates = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #667085;
  line-height: 1.4;
`;

export const PersonalEventsEmpty = styled.p`
  margin: 12px 0 0;
  color: #667085;
  font-size: 14px;
`;

export const PersonalEventFormFields = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const PersonalEventDatePickerHint = styled.p`
  margin: 0;
  color: #667085;
  font-size: 13px;
  line-height: 1.4;
`;

export const PersonalEventSelectedDate = styled.div`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #2f3441;
`;

export const PersonalEventDateList = styled.div`
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const PersonalEventDateChip = styled.button`
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #fff;
  padding: 4px 10px;
  font-size: 13px;
  color: #2f3441;
  cursor: pointer;

  &:hover {
    border-color: #cf1322;
    color: #cf1322;
  }
`;

export const PersonalEventFormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

export const ReminderCard = styled.div`
  border-radius: 10px;
  border: 1px solid #d6d7db;
  padding: 18px;
  box-shadow: 0 6px 28px 0 rgba(120, 130, 140, 0.18);
`;

export const ReminderHint = styled.p`
  margin: 6px 0 14px;
  color: #6f7787;
  font-size: 14px;
`;

export const DetailsHeader = styled.div`
  font-size: ${fontSize.lg};
  line-height: 1.3;
  font-weight: 600;
  color: #2f3441;
`;

export const DetailsContainer = styled.div`
  ul {
    padding-top: 12px;

    li {
      &:before {
        content: '\\2022';
        color: #2f3441;
        font-weight: bold;
        padding-right: 8px;
      }

      padding-bottom: 10px;
      color: #666f7f;
      white-space: pre-wrap;
      font-size: 15px;
    }
  }
`;

export const AddEventWrapper = styled.div`
  text-align: center;
  margin-top: 1rem;
`;

export const LegendWrapper = styled.div`
  border-top: 1px solid #d6d7db;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px 14px;
  padding: 14px 16px 16px;

  ${media.md`
    grid-template-columns: repeat(2, minmax(0, 1fr));
  `}
`;

export const LegendRow = styled.div`
  display: flex;
  align-items: center;
  color: #666f7f;
  font-size: 13px;
`;

interface LegendProps {
  type: 'nabor' | 'party' | 'important' | 'semiImportant' | 'today' | 'ziyarat' | 'selected' | 'gohanbar';
}

const typeStyles = {
  nabor: css`
    background-color: #00800017;
    color: #56b100;
    border: 1px solid #56b100;
  `,
  party: css`
    border: 1px solid #ff0080;
    color: #ff0080;
    background-color: #ff008021;
  `,
  important: css`
    background-color: ${LabelColors.important};
  `,
  ziyarat: css`
    background-color: ${colors.accent};
  `,
  gohanbar: css`
    background-color: ${colors.success};
  `,
  semiImportant: css`
    background-color: ${LabelColors.semiImportant};
  `,
  today: css`
    background-color: ${calendarTodayBg};
    border: 2px solid ${calendarTodayRed};
  `,
  selected: css`
    background-image: ${`linear-gradient(to top, transparent 10%, ${colors.accent} 110%)`};
  `,
} as const;

export const LegendIcon = styled.div<LegendProps>`
  width: 14px;
  height: 14px;
  border-radius: 3px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
  font-size: 0.7rem;
  ${({ type }) => typeStyles[type]}
`;
